import { Box, Table, Sheet, Chip, Tooltip } from "@mui/joy";
import type {
  AlertModelRule,
  EquipmentAttributeDefinition,
  EquipmentModel,
  EquipmentPrefilter,
} from "../../../api/equipment/Equipment.typed";
import { SEdit, SDelete } from "../../../styles/StyledMUI";
import { usePermissions, Permission } from "../../../contexts/app/permissions";
import LimitedChipsTooltip from "./common/LimitedChipsTooltip";
import IconButtonCTA from "../../common/IconButtonCTA";
import { colorTokens } from "../../../styles/globalStyles";

function AttributesChips({
  attributes,
  chipFontSize,
  isSmallMobile,
}: {
  attributes: EquipmentAttributeDefinition[];
  chipFontSize: string;
  isSmallMobile: boolean;
}) {
  if (attributes.length === 0) return null;

  const chipSize = "sm";
  const screenWidth = window.innerWidth;
  const isMobile = screenWidth < 768;

  return (
    <LimitedChipsTooltip
      items={attributes}
      dynamicLimit={true}
      renderChip={(a: EquipmentAttributeDefinition) => (
        <Chip
          key={a.ID}
          size={chipSize as "sm" | "md" | "lg"}
          variant="soft"
          color="neutral"
          sx={{ fontSize: chipFontSize }}
        >
          {a.Label}
        </Chip>
      )}
      renderTooltipItem={(a: EquipmentAttributeDefinition) => (
        <Chip
          key={a.ID}
          size="sm"
          variant="soft"
          color="neutral"
          sx={{
            fontSize: isSmallMobile ? "0.55rem" : isMobile ? "0.6rem" : "0.65rem",
            width: isSmallMobile ? "100%" : "auto",
            minHeight: isSmallMobile ? "28px" : "24px",
            padding: isSmallMobile ? "4px 8px" : "2px 6px",
            maxWidth: "100%",
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          {a.Label}
        </Chip>
      )}
    />
  );
}

function PrefiltersChips({
  prefilters,
  chipFontSize,
  isSmallMobile,
}: {
  prefilters: EquipmentPrefilter[];
  chipFontSize: string;
  isSmallMobile: boolean;
}) {
  const chipSize = "sm";

  const screenWidth = window.innerWidth;
  const isMobile = screenWidth < 768;

  if (!prefilters || prefilters.length === 0) {
    return (
      <Chip
        size={chipSize as "sm" | "md" | "lg"}
        variant="outlined"
        color="neutral"
        sx={{
          color: colorTokens.text.muted,
          borderColor: colorTokens.border.default,
          fontSize: chipFontSize,
        }}
      >
        Aucun
      </Chip>
    );
  }

  return (
    <LimitedChipsTooltip
      items={prefilters}
      dynamicLimit={true}
      renderChip={(pf: EquipmentPrefilter) => (
        <Chip
          key={pf.ID}
          size={chipSize as "sm" | "md" | "lg"}
          variant="soft"
          color="primary"
          sx={{
            fontWeight: 500,
            fontSize: chipFontSize,
          }}
        >
          {pf.Name}
        </Chip>
      )}
      renderTooltipItem={(pf: EquipmentPrefilter) => (
        <Chip
          key={pf.ID}
          size="sm"
          variant="soft"
          color="primary"
          sx={{
            fontSize: isSmallMobile ? "0.55rem" : isMobile ? "0.6rem" : "0.65rem",
            width: isSmallMobile ? "100%" : "auto",
            minHeight: isSmallMobile ? "28px" : "24px",
            padding: isSmallMobile ? "4px 8px" : "2px 6px",
            maxWidth: "100%",
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          {pf.Name}
        </Chip>
      )}
    />
  );
}

function AlertsChips({
  alerts,
  chipFontSize,
  isSmallMobile,
}: {
  alerts: AlertModelRule[];
  chipFontSize: string;
  isSmallMobile: boolean;
}) {
  const chipSize = "sm";

  const screenWidth = window.innerWidth;
  const isMobile = screenWidth < 768;

  if (!alerts || alerts.length === 0) {
    return (
      <Chip
        size={chipSize as "sm" | "md" | "lg"}
        variant="outlined"
        color="neutral"
        sx={{
          color: colorTokens.text.muted,
          borderColor: colorTokens.border.default,
          fontSize: chipFontSize,
        }}
      >
        Aucune
      </Chip>
    );
  }

  return (
    <LimitedChipsTooltip
      items={alerts}
      dynamicLimit={true}
      renderChip={(rule: AlertModelRule) => (
        <Chip
          key={rule.ID}
          size={chipSize as "sm" | "md" | "lg"}
          variant="soft"
          color={
            rule.Severity === "critical"
              ? "danger"
              : rule.Severity === "warning"
                ? "warning"
                : "neutral"
          }
          sx={{ fontWeight: 500, fontSize: chipFontSize }}
        >
          {rule.Name}
        </Chip>
      )}
      renderTooltipItem={(rule: AlertModelRule) => (
        <Chip
          key={rule.ID}
          size="sm"
          variant="soft"
          color={
            rule.Severity === "critical"
              ? "danger"
              : rule.Severity === "warning"
                ? "warning"
                : "neutral"
          }
          sx={{
            fontSize: isSmallMobile ? "0.55rem" : isMobile ? "0.6rem" : "0.65rem",
            width: isSmallMobile ? "100%" : "auto",
            minHeight: isSmallMobile ? "28px" : "24px",
            padding: isSmallMobile ? "4px 8px" : "2px 6px",
            maxWidth: "100%",
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          {rule.Name}
        </Chip>
      )}
    />
  );
}

interface EquipmentModelsTableProps {
  models: EquipmentModel[];
  onEditModel: (model: EquipmentModel) => void;
  onDeleteModel: (id?: number) => void;
}

export default function EquipmentModelsTable({
  models,
  onEditModel,
  onDeleteModel,
}: EquipmentModelsTableProps) {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.EQUIPMENT_EDIT);

  const screenWidth = window.innerWidth;
  const isMobile = screenWidth < 768;
  const isSmallMobile = screenWidth < 480;

  const headerFontSize = isSmallMobile ? "0.7rem" : isMobile ? "0.8rem" : "0.9rem";
  const cellFontSize = isSmallMobile ? "0.65rem" : isMobile ? "0.75rem" : "0.85rem";
  const chipFontSize = isSmallMobile ? "0.6rem" : isMobile ? "0.65rem" : "0.7rem";
  const iconSize = "sm";
  const tableSize = isSmallMobile ? "sm" : "md";
  const tablePadding = isSmallMobile ? 0.5 : isMobile ? 1 : 2;

  return (
    <Sheet variant="outlined" sx={{ p: 0, borderRadius: 4, overflow: "hidden" }}>
      <Table
        hoverRow
        size={tableSize as "sm" | "md" | "lg"}
        sx={{
          "--TableCell-headBackground": colorTokens.bg.surfaceMuted,
          "& th": {
            whiteSpace: isSmallMobile ? "normal" : "nowrap",
            color: colorTokens.text.secondary,
            fontSize: headerFontSize,
            padding: `${tablePadding}px`,
          },
          "& td": {
            backgroundColor: colorTokens.bg.app,
            color: colorTokens.text.secondary,
            fontSize: cellFontSize,
            padding: `${tablePadding}px`,
          },
          "& tr:hover td": { backgroundColor: colorTokens.bg.surface },
        }}
      >
        <thead>
          <tr>
            <th>Nom</th>
            <th>Attributs</th>
            <th>Filtres de recherche</th>
            <th>Alertes</th>
            <th style={{ width: 96 }}></th>
          </tr>
        </thead>
        <tbody>
          {models.map((m) => (
            <tr key={m.ID} style={{ cursor: "pointer" }}>
              <td onClick={() => onEditModel(m)}>{m.Name}</td>
              <td onClick={() => onEditModel(m)}>
                <AttributesChips
                  attributes={m.Attributes || []}
                  chipFontSize={chipFontSize}
                  isSmallMobile={isSmallMobile}
                />
              </td>
              <td onClick={() => onEditModel(m)}>
                <PrefiltersChips
                  prefilters={m.Prefilters || []}
                  chipFontSize={chipFontSize}
                  isSmallMobile={isSmallMobile}
                />
              </td>
              <td onClick={() => onEditModel(m)}>
                <AlertsChips
                  alerts={m.Alerts || []}
                  chipFontSize={chipFontSize}
                  isSmallMobile={isSmallMobile}
                />
              </td>
              <td>
                {canManage && (
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Tooltip title="Modifier">
                      <IconButtonCTA
                        ctaVariant="accent"
                        size={iconSize as "sm" | "md" | "lg"}
                        aria-label="Modifier modèle"
                        onClick={() => onEditModel(m)}
                      >
                        <SEdit />
                      </IconButtonCTA>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <IconButtonCTA
                        ctaVariant="danger"
                        size={iconSize as "sm" | "md" | "lg"}
                        aria-label="Supprimer modèle"
                        onClick={() => onDeleteModel(m.ID)}
                      >
                        <SDelete />
                      </IconButtonCTA>
                    </Tooltip>
                  </Box>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Sheet>
  );
}
